/**
 * The interview as a candidate will experience it, in order. The questions are
 * examples: Rio adapts each candidate's questions to the gaps between their
 * résumé and the job, so the page says so rather than implying a script.
 */
import { useId } from 'react';
import type { InterviewerPreview } from '../../services/interviewer.types';
import styles from './index.module.css';

interface Props {
  preview: InterviewerPreview;
}

const CandidatePreview = ({ preview }: Props) => {
  const ids = useId();
  const on = preview.sections.filter((s) => s.enabled);
  const off = preview.sections.filter((s) => !s.enabled);
  const total = preview.totalMinutes;

  return (
    <section className={styles.panel} aria-labelledby={`${ids}-title`}>
      <div className={styles.previewHead}>
        <h2 className={styles.briefTitle} id={`${ids}-title`}>
          What candidates will experience
        </h2>
        <span className={styles.stepMinutes}>About {total} min</span>
      </div>
      <div className={styles.previewBody}>
        <p className={styles.hint}>
          The questions below are examples. Each candidate&rsquo;s questions
          adapt to the gaps between their résumé and this job.
        </p>
        <ol className={styles.steps}>
          <li className={styles.step}>
            <span className={styles.stepTitle}>Welcome</span>
            <span className={styles.stepMinutes} />
            <p className={styles.stepSummary}>
              Rio introduces itself, how long it takes, and who sees the
              answers.
            </p>
          </li>
          {on.map((section) => {
            const own = preview.customQuestions.filter(
              (q) => q.section === section.key,
            );
            const isTask = section.key === 'workspace';
            return (
              <li key={section.key} className={styles.step}>
                <span className={styles.stepTitle}>{section.title}</span>
                <span className={styles.stepMinutes}>
                  {section.minutes} min
                </span>
                <p className={styles.stepSummary}>
                  {isTask && preview.workspace.enabled
                    ? preview.workspace.label
                    : section.summary}
                </p>
                {(section.exampleQuestions.length > 0 || own.length > 0) && (
                  <ul className={styles.examples}>
                    {section.exampleQuestions.map((q) => (
                      <li key={q} className={styles.example}>
                        <span className={styles.exampleTag}>Example</span>
                        &ldquo;{q}&rdquo;
                      </li>
                    ))}
                    {own.map((q) => (
                      <li
                        key={q.id}
                        className={`${styles.example} ${styles.exampleOwn}`}
                      >
                        <span className={styles.exampleTag}>
                          Your question
                          {q.counts && q.areaLabel
                            ? `, counts toward ${q.areaLabel}`
                            : q.counts
                              ? ''
                              : ', for information only'}
                        </span>
                        &ldquo;{q.text}&rdquo;
                      </li>
                    ))}
                  </ul>
                )}
                {isTask && preview.workspace.note && (
                  <p className={styles.stepSummary}>{preview.workspace.note}</p>
                )}
              </li>
            );
          })}
          <li className={styles.step}>
            <span className={styles.stepTitle}>Wrap-up</span>
            <span className={styles.stepMinutes} />
            <p className={styles.stepSummary}>
              Anything they want to add, and what happens next.
            </p>
          </li>
        </ol>
        <div className={styles.previewMeta}>
          {preview.emphasis.emphasize.length > 0 && (
            <span>
              <b>More focus:</b>{' '}
              {preview.emphasis.emphasize.map((s) => s.label).join(', ')}
            </span>
          )}
          {preview.emphasis.deemphasize.length > 0 && (
            <span>
              <b>Less focus:</b>{' '}
              {preview.emphasis.deemphasize.map((s) => s.label).join(', ')}
            </span>
          )}
          {off.length > 0 && (
            <span>
              <b>Skipped:</b> {off.map((s) => s.title).join(', ')}
            </span>
          )}
          {preview.notes.map((note) => (
            <span key={note}>{note}</span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CandidatePreview;
